// DS: TA0.3
import React, { useState, useCallback } from 'react';
import { PapiUser } from '@wix/da-papi-types';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import PlusIcon from '@wix/da-ds/pkg/Icons/24x24/Add';
import CheckIcon from '@wix/da-ds/pkg/Icons/24x24/Check';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { useWatchButton } from '../../WatchButton/useWatchButton';

export type Props = {
  user: PapiUser;
  className?: string;
  disabled?: boolean;
  disableClickHandler?: boolean;
  initialData?: { isWatching: boolean; isLoading: boolean };
  onClick?: (e: React.MouseEvent) => void;
  onToggleWatchSuccess?: (isWatchingBack?: boolean) => void;
  renderCustomText?: (isWatching: boolean) => string;
  isWatchingBack?: boolean;
};

const ProfileOwnerWatchButton: React.FC<React.PropsWithChildren<Props>> = ({
  disabled,
  className,
  disableClickHandler,
  user,
  initialData,
  onClick: onClickProp,
  renderCustomText,
  onToggleWatchSuccess,
  isWatchingBack = false,
}) => {
  const { text, isWatching, isLoading, biData, onClick } = useWatchButton(
    user,
    user.isWatching ?? initialData?.isWatching,
    isWatchingBack,
    disabled,
    onToggleWatchSuccess
  );
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isNewWatching, setIsNewWatching] = useState(false);

  const handleClick = useCallback(
    e => {
      if (disableClickHandler) {
        return;
      }
      setIsNewWatching(!isWatching);
      onClick();
      onClickProp && onClickProp(e);
    },
    [disableClickHandler, isWatching, onClick, onClickProp]
  );

  const handleMouseLeave = useCallback(e => {
    // when you click on button's child elements,
    // the mouseleave event triggers
    if (e.currentTarget !== e.target) {
      return;
    }
    setIsNewWatching(false);
    setIsMouseOver(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsMouseOver(true);
  }, []);

  const Button: any = isWatching ? SolidButton : BrandedButton;
  const buttonVariantProps = isWatching
    ? { variant: 'approval-green', priority: 'secondary' }
    : { variant: 'brand-green' };
  let icon = PlusIcon;
  if (isWatching && !isLoading) {
    icon = CheckIcon;
    if (isMouseOver && !isNewWatching) {
      icon = CloseIcon;
    }
  }

  return (
    <Button
      size="large"
      {...buttonVariantProps}
      icon={icon}
      className={className}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      loading={isLoading}
      disabled={disabled}
      biData={biData}
    >
      {renderCustomText ? renderCustomText(isWatching) : text}
    </Button>
  );
};
ProfileOwnerWatchButton.displayName = 'ProfileOwnerWatchButton';

export default ProfileOwnerWatchButton;
